import React, { useState, useEffect } from 'react';
import {
  RUSSIA_REGIONS,
  getSubjects,
  getDistricts,
  getSettlements,
  type Settlement,
} from '../../shared/data/russiaRegions';

type RelationshipType = 'romantic' | 'friend' | 'colleague' | 'family' | 'other';

const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  romantic: 'Романтические',
  friend: 'Дружба',
  colleague: 'Коллеги',
  family: 'Семья',
  other: 'Другое',
};

interface Settlement {
  name: string;
  type: string;
  latitude: number;
  longitude: number;
}

export interface ContactFormData {
  name: string;
  birthDate: string;
  birthTime?: string;
  birthPlace?: {
    city?: string;
    country?: string;
    latitude: number;
    longitude: number;
    timezone?: string;
    timezoneOffset?: number;
  };
  birthPlaceFull?: string;
  relationshipType?: string;
  notes?: string;
}

export interface ContactFormProps {
  contact?: ContactFormData | null;
  mode: 'create' | 'edit';
  onSave: (data: ContactFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  mode,
  onSave,
  onCancel,
  loading = false,
  error,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formName, setFormName] = useState('');
  const [formBirthDate, setFormBirthDate] = useState('');
  const [formBirthTime, setFormBirthTime] = useState('');
  const [formFederalDistrict, setFormFederalDistrict] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formDistrict, setFormDistrict] = useState('');
  const [formSettlement, setFormSettlement] = useState('');
  const [formLat, setFormLat] = useState('');
  const [formLon, setFormLon] = useState('');
  const [formRelType, setFormRelType] = useState<RelationshipType>('romantic');
  const [formNotes, setFormNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Pre-fill when editing
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    console.log('[ContactForm] mode:', mode, 'contact:', JSON.stringify(contact, null, 2));
    if (contact && mode === 'edit') {
      setFormName(contact.name);
      // Форматируем дату: "1980-04-24T00:00:00.000Z" → "1980-04-24"
      const formattedDate = contact.birthDate
        ? contact.birthDate.split('T')[0]
        : '';
      setFormBirthDate(formattedDate);
      setFormBirthTime(contact.birthTime || '');
      setFormRelType((contact.relationshipType as RelationshipType) || 'romantic');
      setFormNotes(contact.notes || '');
      if (contact.birthPlace) {
        // Всегда восстанавливаем координаты из сохранённых данных
        setFormLat(String(contact.birthPlace.latitude ?? ''));
        setFormLon(String(contact.birthPlace.longitude ?? ''));

        // Восстанавливаем location из birthPlaceFull (4 части)
        if (contact.birthPlaceFull) {
          const parts = contact.birthPlaceFull.split(', ').map(s => s.trim());
          if (parts.length >= 4) {
            setFormSettlement(parts[0]);
            setFormDistrict(parts[1]);
            setFormSubject(parts[2]);
            setFormFederalDistrict(parts[3]);
          } else if (parts.length < 4 && contact.birthPlace.city) {
            // Reverse-lookup: ищем settlement по имени city
            reverseLookupCity(contact.birthPlace.city);
          }
        } else if (contact.birthPlace.city) {
          // Нет birthPlaceFull, только city — ищем его в справочнике
          reverseLookupCity(contact.birthPlace.city);
        }
      }
    }
    return () => window.removeEventListener('resize', handler);
  }, [contact, mode]);

  // Reverse-lookup: ищем settlement по имени и проставляем каскадные селекты
  const reverseLookupCity = (cityName: string) => {
    const normalized = cityName.trim().toLowerCase();

    for (const fd of RUSSIA_REGIONS) {
      for (const subject of fd.subjects) {
        for (const district of subject.districts) {
          for (const settlement of district.settlements) {
            if (settlement.name.toLowerCase() === normalized) {
              console.log(`[ContactForm] Reverse-lookup found: ${settlement.name} → ${fd.name} → ${subject.name} → ${district.name}`);
              setFormFederalDistrict(fd.name);
              setFormSubject(subject.name);
              setFormDistrict(district.name);
              setFormSettlement(settlement.name);
              return;
            }
          }
        }
      }
    }

    console.log(`[ContactForm] Reverse-lookup NOT found for: "${cityName}"`);
  };

  const resetForm = () => {
    setFormName('');
    setFormBirthDate('');
    setFormBirthTime('');
    setFormFederalDistrict('');
    setFormSubject('');
    setFormDistrict('');
    setFormSettlement('');
    setFormLat('');
    setFormLon('');
    setFormRelType('romantic');
    setFormNotes('');
    setFormError(null);
  };

  // Auto-set coordinates when settlement changes
  useEffect(() => {
    if (formFederalDistrict && formSubject && formDistrict && formSettlement) {
      const settlements = getSettlements(formFederalDistrict, formSubject, formDistrict);
      const found = settlements.find(s => s.name === formSettlement);
      if (found) {
        setFormLat(String(found.latitude));
        setFormLon(String(found.longitude));
      }
    }
  }, [formFederalDistrict, formSubject, formDistrict, formSettlement]);

  const handleSave = async () => {
    setFormError(null);

    if (!formName.trim()) {
      setFormError('Введите имя');
      return;
    }
    if (!formBirthDate) {
      setFormError('Выберите дату рождения');
      return;
    }

    // При создании — координаты обязательны, при редактировании — можно оставить как есть
    const needsCoordinates = mode === 'create';
    let lat: number | undefined;
    let lon: number | undefined;

    if (formLat && formLon) {
      lat = parseFloat(formLat);
      lon = parseFloat(formLon);
      if (isNaN(lat) || isNaN(lon)) {
        setFormError('Координаты должны быть числами');
        return;
      }
    } else if (needsCoordinates) {
      setFormError('Укажите координаты места рождения');
      return;
    }

    await onSave({
      name: formName.trim(),
      birthDate: formBirthDate,
      birthTime: formBirthTime || undefined,
      birthPlace: lat !== undefined && lon !== undefined
        ? {
            city: formSettlement.trim() || formDistrict.trim() || 'Неизвестно',
            country: 'Россия',
            latitude: lat,
            longitude: lon,
            timezone: 'UTC',
            timezoneOffset: 0,
          }
        : undefined,
      birthPlaceFull: formSettlement
        ? `${formSettlement}, ${formDistrict}, ${formSubject}, ${formFederalDistrict}`
        : undefined,
      relationshipType: formRelType,
      notes: formNotes.trim() || undefined,
    });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px',
    background: 'rgba(13, 20, 40, 0.6)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    borderRadius: '12px', color: '#fff', fontSize: isMobile ? '14px' : '15px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', color: '#b0b0b0', marginBottom: '6px', fontSize: '13px',
  };

  return (
    <div>
      <h2 style={{
        color: '#f0f0f0', fontSize: isMobile ? '18px' : '22px', marginBottom: '24px', marginTop: 0,
        fontFamily: 'var(--font-display)', fontStyle: 'italic',
        background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
        backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
      }}>
        {mode === 'create' ? 'Новый контакт' : 'Редактировать контакт'}
      </h2>

      {formError && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.15)',
          border: '1px solid rgba(220, 53, 69, 0.3)',
          color: '#dc3545', padding: '10px 14px', borderRadius: '10px',
          fontSize: '14px', marginBottom: '8px',
        }}>
          {formError}
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.15)',
          border: '1px solid rgba(220, 53, 69, 0.3)',
          color: '#dc3545', padding: '10px 14px', borderRadius: '10px',
          fontSize: '14px', marginBottom: '8px',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        <div>
          <label style={labelStyle}>Имя *</label>
          <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)}
            placeholder="Имя человека" style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>Дата рождения *</label>
          <input type="date" value={formBirthDate} onChange={(e) => setFormBirthDate(e.target.value)}
            style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>Время рождения (необязательно)</label>
          <input type="time" value={formBirthTime} onChange={(e) => setFormBirthTime(e.target.value)}
            style={inputStyle} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Федеральный округ</label>
            <select value={formFederalDistrict}
              onChange={(e) => { setFormFederalDistrict(e.target.value); setFormSubject(''); setFormDistrict(''); setFormSettlement(''); }}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">Выберите округ</option>
              {RUSSIA_REGIONS.map((fd) => (
                <option key={fd.name} value={fd.name} style={{ background: '#1a1a3e' }}>{fd.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Субъект</label>
            <select value={formSubject}
              onChange={(e) => { setFormSubject(e.target.value); setFormDistrict(''); setFormSettlement(''); }}
              disabled={!formFederalDistrict}
              style={{ ...inputStyle, cursor: formFederalDistrict ? 'pointer' : 'not-allowed' }}>
              <option value="">Выберите субъект</option>
              {formFederalDistrict && getSubjects(formFederalDistrict).map((s) => (
                <option key={s.name} value={s.name} style={{ background: '#1a1a3e' }}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Район</label>
            <select value={formDistrict}
              onChange={(e) => { setFormDistrict(e.target.value); setFormSettlement(''); }}
              disabled={!formSubject}
              style={{ ...inputStyle, cursor: formSubject ? 'pointer' : 'not-allowed' }}>
              <option value="">Выберите район</option>
              {formFederalDistrict && formSubject && getDistricts(formFederalDistrict, formSubject).map((d) => (
                <option key={d.name} value={d.name} style={{ background: '#1a1a3e' }}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Населённый пункт</label>
            <select value={formSettlement}
              onChange={(e) => setFormSettlement(e.target.value)}
              disabled={!formDistrict}
              style={{ ...inputStyle, cursor: formDistrict ? 'pointer' : 'not-allowed' }}>
              <option value="">Выберите населённый пункт</option>
              {formFederalDistrict && formSubject && formDistrict
                ? getSettlements(formFederalDistrict, formSubject, formDistrict).map((s) => (
                    <option key={s.name} value={s.name} style={{ background: '#1a1a3e' }}>
                      {s.name} ({s.type === 'city' ? 'город' : s.type === 'town' ? 'посёлок' : s.type === 'village' ? 'деревня' : 'село'})
                    </option>
                  ))
                : null}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Широта (авто)</label>
            <input type="number" step="0.0001" value={formLat} readOnly
              placeholder="Выбери город"
              style={{ ...inputStyle, opacity: 0.7, cursor: 'not-allowed' }} />
          </div>
          <div>
            <label style={labelStyle}>Долгота (авто)</label>
            <input type="number" step="0.0001" value={formLon} readOnly
              placeholder="Выбери город"
              style={{ ...inputStyle, opacity: 0.7, cursor: 'not-allowed' }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Тип отношений</label>
          <select value={formRelType} onChange={(e) => setFormRelType(e.target.value as RelationshipType)}
            style={{ ...inputStyle, cursor: 'pointer' }}>
            {Object.entries(RELATIONSHIP_LABELS).map(([key, label]) => (
              <option key={key} value={key} style={{ background: '#1a1a3e' }}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Заметки</label>
          <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)}
            placeholder="Дополнительная информация..." rows={3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: isMobile ? 'wrap' as const : 'nowrap' as const }}>
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            background: loading ? '#666' : 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
            border: 'none', padding: isMobile ? '10px 20px' : '12px 30px', borderRadius: '12px',
            color: '#0A0814', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}>
          {loading ? 'Сохранение...' : mode === 'create' ? 'Сохранить' : 'Обновить'}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          style={{
            background: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid rgba(212, 175, 55, 0.12)',
            padding: isMobile ? '10px 20px' : '12px 25px', borderRadius: '12px',
            color: '#b0b0b0', fontSize: isMobile ? '14px' : '16px', cursor: loading ? 'not-allowed' : 'pointer',
          }}>
          Отмена
        </button>
      </div>
    </div>
  );
};

export default ContactForm;
